/**
 * Spec 107 — Page-Level Persona Evaluation. Typed client for brand-scoped page
 * persona-fit routes + the read-only three-lens composition endpoint.
 *
 *   POST /api/v1/brands/:brandId/page-persona-evaluations
 *   GET  /api/v1/brands/:brandId/page-persona-evaluations
 *   GET  /api/v1/brands/:brandId/page-persona-evaluations/:evalId
 *   GET  /api/v1/brands/:brandId/page-persona-evaluations/three-lens?url=&persona_id=
 *
 * The page persona-fit lens is a CONTENT-QUALITY HYPOTHESIS — never blended with
 * the GEO score (083) or the response persona-fit (106).
 */
import { apiGet } from '../lib/api.js';
import { apiPost, apiDelete } from '../lib/api-mutations.js';
import type { PersonaConfidence, PersonaType } from './personas.js';

export type PageDimensionType =
  | 'priority'
  | 'trust_provision'
  | 'anti_signal'
  | 'wellbeing';

export type PagePersonaEvalStatus =
  | 'pending'
  | 'running'
  | 'completed'
  | 'unevaluable'
  | 'failed';

export interface PagePersonaDimensionResult {
  id: string;
  page_persona_evaluation_id: string;
  dimension_type: PageDimensionType;
  dimension_label: string;
  score: number;
  reasoning: string | null;
  supporting_quote: string | null;
  source_dimension_id: string | null;
  weight: number;
}

export interface CaveatMeta {
  content_quality_label: string;
  persona_definition_caveat: string;
  page_encounter_caveat: string;
}

export interface PagePersonaEvaluation {
  id: string;
  brand_id: string;
  persona_id: string;
  persona_stable_id: string;
  persona_version: number;
  persona_confidence_at_eval: PersonaConfidence;
  persona_type: PersonaType;
  page_url: string;
  page_url_hash: string;
  page_content_snapshot_id: string | null;
  page_content_hash: string | null;
  rubric_version: string | null;
  judge_model_version: string | null;
  evaluation_status: PagePersonaEvalStatus;
  unevaluable_reason: string | null;
  page_priority_fit_score: number | null;
  page_wellbeing_fit_score: number | null;
  trust_provision_score: number | null;
  anti_signal_penalty: number | null;
  lens_label: string;
  error: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  dimension_results?: PagePersonaDimensionResult[];
  caveats?: CaveatMeta;
}

export interface GeoLensView {
  claim_type: 'compliance_audience_agnostic';
  evaluation_id: string;
  page_url: string;
  status: string;
  aggregate_score: number | null;
  rules_evaluated: number;
  completed_at: string | null;
}

export interface ResponsePersonaLensView {
  claim_type: 'observational';
  finding_id: string;
  persona_stable_id: string;
  status: string;
  observed_visibility: number | null;
  priority_fit: number | null;
  wellbeing_fit: number | null;
  trust: number | null;
  anti_signal_penalty: number | null;
  completed_at: string | null;
}

export interface PagePersonaLensView {
  claim_type: 'content_quality_hypothesis';
  evaluation_id: string;
  persona_stable_id: string;
  status: PagePersonaEvalStatus;
  unevaluable_reason: string | null;
  page_priority_fit_score: number | null;
  page_wellbeing_fit_score: number | null;
  trust_provision_score: number | null;
  anti_signal_penalty: number | null;
  completed_at: string | null;
}

export interface ThreeLensComposition {
  page_url: string;
  persona_id: string | null;
  geo: GeoLensView | null;
  response_persona_fit: ResponsePersonaLensView | null;
  page_persona_fit: PagePersonaLensView | null;
}

export async function listPagePersonaEvaluations(
  brandId: string,
  filters: { personaId?: string; status?: string; url?: string } = {},
): Promise<PagePersonaEvaluation[]> {
  const params = new URLSearchParams();
  if (filters.personaId) params.set('persona_id', filters.personaId);
  if (filters.status) params.set('status', filters.status);
  if (filters.url) params.set('url', filters.url);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const res = await apiGet<{ data: PagePersonaEvaluation[] }>(
    `/api/v1/brands/${brandId}/page-persona-evaluations${qs}`,
  );
  return Array.isArray(res.data) ? res.data : [];
}

export async function getPagePersonaEvaluation(
  brandId: string,
  evalId: string,
): Promise<PagePersonaEvaluation> {
  const res = await apiGet<{ data: PagePersonaEvaluation }>(
    `/api/v1/brands/${brandId}/page-persona-evaluations/${evalId}`,
  );
  return res.data;
}

/**
 * Spec 215 — fetch ONE evaluation rendered as markdown, plus the filename the
 * server derived for it. The filename comes from the server so the slug rules
 * live beside the content that justifies them, rather than being re-derived
 * here and drifting.
 */
export async function getPagePersonaEvaluationMarkdown(
  brandId: string,
  evalId: string,
): Promise<{ markdown: string; filename: string }> {
  const res = await apiGet<{ data: { markdown: string; filename: string } }>(
    `/api/v1/brands/${brandId}/page-persona-evaluations/${evalId}/markdown`,
  );
  return res.data;
}

/* ---------------------------------------------------------------- */
/* Spec 219 - bulk page persona-fit                                  */
/* ---------------------------------------------------------------- */

export interface BulkCell {
  domain: string;
  url: string;
  mention_count: number;
  persona_stable_id: string;
  already_complete: boolean;
}

export interface BulkPreview {
  cells: BulkCell[];
  total_cells: number;
  already_complete: number;
  to_run: number;
  /** null when nothing has recorded a cost yet - never a guessed number. */
  estimated_cost_usd: number | null;
  estimate_basis: string;
  judge_model: string;
  url_measure: string;
  /** Derived from the matrix; `run` refuses anything that does not match. */
  confirm_token: string;
}

export interface BulkSelection {
  domains: string[];
  urls_per_domain: number;
  persona_stable_ids: string[];
}

export async function previewBulkPagePersonaFit(
  brandId: string,
  sel: BulkSelection,
): Promise<BulkPreview> {
  const res = await apiPost<{ data: BulkPreview }>(
    `/api/v1/brands/${brandId}/bulk-page-persona-fit/preview`,
    sel,
  );
  return res.data;
}

export async function runBulkPagePersonaFit(
  brandId: string,
  cells: Array<{ url: string; persona_stable_id: string }>,
  confirmToken: string,
): Promise<{ queued: number; skipped_existing: number }> {
  const res = await apiPost<{ data: { queued: number; skipped_existing: number } }>(
    `/api/v1/brands/${brandId}/bulk-page-persona-fit/run`,
    { cells, confirm_token: confirmToken },
  );
  return res.data;
}

export async function getBulkPagePersonaFitReport(
  brandId: string,
  sel: BulkSelection,
): Promise<{ markdown: string; filename: string; completed: number; total: number }> {
  const res = await apiPost<{
    data: { markdown: string; filename: string; completed: number; total: number };
  }>(`/api/v1/brands/${brandId}/bulk-page-persona-fit/report`, sel);
  return res.data;
}

export async function runPagePersonaEvaluation(
  brandId: string,
  input: { page_url: string; persona_id: string; force_refresh?: boolean },
): Promise<{ evaluation_id: string; status: PagePersonaEvalStatus }> {
  const res = await apiPost<{
    data: { evaluation_id: string; status: PagePersonaEvalStatus };
  }>(`/api/v1/brands/${brandId}/page-persona-evaluations`, input);
  return res.data;
}

/** Spec 115 — delete one page persona-fit evaluation (brand-scoped, admin). */
export async function deletePagePersonaEvaluation(
  brandId: string,
  evalId: string,
): Promise<void> {
  await apiDelete(`/api/v1/brands/${brandId}/page-persona-evaluations/${evalId}`);
}

export async function getThreeLens(
  brandId: string,
  url: string,
  personaId: string,
): Promise<ThreeLensComposition> {
  const params = new URLSearchParams();
  params.set('url', url);
  params.set('persona_id', personaId);
  const res = await apiGet<{ data: ThreeLensComposition }>(
    `/api/v1/brands/${brandId}/page-persona-evaluations/three-lens?${params.toString()}`,
  );
  return res.data;
}
